import React from 'react';
import Overlay from 'react-bootstrap/Overlay';
import Popover from 'react-bootstrap/Popover';
import {Button} from '@/components/ui';
import CellLoader from '@/components/loading/CellLoader';
import Icon from '@/components/icons/Icon';
import i18n from 'i18n';

interface Props {
    target: HTMLElement | null;
    show: boolean;
    activeWord: string;
    similarWords: string[];
    loading: boolean;
    existingWords: string[];
    onHide: () => void;
    onAddWord: (word: string) => void;
}

const QuerySimilarWordsPopover = React.memo<Props>((
    {
        target,
        show,
        activeWord,
        similarWords,
        loading,
        existingWords,
        onHide,
        onAddWord,
    },
) => {
    if (!activeWord) {
        return null;
    }

    let body: React.ReactNode;

    if (loading) {
        body = <CellLoader fullHeight={false} />;
    } else if (!similarWords.length) {
        body = (
            <div className="similar-words-item similar-words-item--error p-1">
                {i18n.t('similar_words_empty.text')}
            </div>
        );
    } else {
        body = (
            <>
                {similarWords.map((entry) => {
                    const word = Array.isArray(entry) ? entry[0] : entry;

                    if (existingWords.includes(word)) {
                        return null;
                    }

                    if (word === i18n.t('word2vec_not.text')) {
                        return (
                            <div key={String(word)} className="similar-words-item similar-words-item--error p-1">
                                {word}
                            </div>
                        );
                    }

                    return (
                        <div
                            key={word}
                            className="similar-words-item d-flex align-items-center p-1"
                        >
                            <Button
                                variant="outline-info"
                                size="sm"
                                className="similar-words-add-btn"
                                title={i18n.t('es_add_input.text')}
                                onClick={() => onAddWord(word)}
                            >
                                <Icon name="mdi-plus" size="xs" />
                            </Button>
                            <span className="similar-words-item-word">{word}</span>
                        </div>
                    );
                })}
            </>
        );
    }

    return (
        <Overlay
            target={target}
            show={show}
            placement="bottom-start"
            container={typeof document !== 'undefined' ? document.body : undefined}
            rootClose
            onHide={onHide}
        >
            <Popover id="query-similar-words-popover">
                <Popover.Header as="h3">
                    {`${i18n.t('similar_words.text')} `}
                    {activeWord}
                </Popover.Header>
                <Popover.Body
                    style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {body}
                </Popover.Body>
            </Popover>
        </Overlay>
    );
});

export default QuerySimilarWordsPopover;
